/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  Bell,
  Building2,
  ChevronLeft,
  ChevronRight,
  DoorOpen,
  ListChecks,
  Lock,
  type LucideIcon,
  SlidersHorizontal,
  Tag,
  UserCog,
} from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChangePasswordForm } from '../../components/account/change-password-form.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { useBillingSettings } from '../../lib/queries/billing-settings.js';
import { useClinicProfile, useNotificationPrefs } from '../../lib/queries/clinic.js';

import { BillingSettingsForm } from './billing-settings-form.js';
import { NotificationsForm } from './notifications-form.js';
import { RoomsPanel } from './rooms-panel.js';
import { SessionRulesPanel } from './session-rules-panel.js';
import { SettingsForm } from './settings-form.js';
import { TherapyTypesPanel } from './therapy-types-panel.js';

type Section =
  | 'profile'
  | 'therapyTypes'
  | 'rooms'
  | 'sessionRules'
  | 'notifications'
  | 'billing'
  | 'security';

interface CardDef {
  readonly key: Section | 'users';
  readonly icon: LucideIcon;
  /** Navigate instead of opening an in-page panel (Users & Roles lives on its own page). */
  readonly to?: string;
}

const CARDS: readonly CardDef[] = [
  { key: 'profile', icon: Building2 },
  { key: 'users', icon: UserCog, to: '/admin/users' },
  { key: 'therapyTypes', icon: Tag },
  { key: 'sessionRules', icon: ListChecks },
  { key: 'billing', icon: SlidersHorizontal },
  { key: 'notifications', icon: Bell },
  { key: 'rooms', icon: DoorOpen },
  { key: 'security', icon: Lock },
];
const LOAD_ERROR = 'common.loadError';

function ProfilePanel(): JSX.Element {
  const { t } = useTranslation();
  const profile = useClinicProfile();
  return (
    <QueryState
      isLoading={profile.isLoading}
      isError={profile.isError}
      isEmpty={false}
      loadingLabel={t('common.loading')}
      errorLabel={t(LOAD_ERROR)}
      emptyLabel={t(LOAD_ERROR)}
    >
      {profile.data !== undefined && <SettingsForm profile={profile.data} />}
    </QueryState>
  );
}

function NotificationsPanel(): JSX.Element {
  const { t } = useTranslation();
  const prefs = useNotificationPrefs();
  return (
    <QueryState
      isLoading={prefs.isLoading}
      isError={prefs.isError}
      isEmpty={false}
      loadingLabel={t('common.loading')}
      errorLabel={t(LOAD_ERROR)}
      emptyLabel={t(LOAD_ERROR)}
    >
      {prefs.data !== undefined && <NotificationsForm prefs={prefs.data} />}
    </QueryState>
  );
}

function BillingPanel(): JSX.Element {
  const { t } = useTranslation();
  const settings = useBillingSettings();
  return (
    <QueryState
      isLoading={settings.isLoading}
      isError={settings.isError}
      isEmpty={false}
      loadingLabel={t('common.loading')}
      errorLabel={t(LOAD_ERROR)}
      emptyLabel={t(LOAD_ERROR)}
    >
      {settings.data !== undefined && <BillingSettingsForm settings={settings.data} />}
    </QueryState>
  );
}

function SecurityPanel(): JSX.Element {
  const { t } = useTranslation();
  const noop = useCallback(() => undefined, []);
  return (
    <div className="max-w-md space-y-4">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('settings.security.subtitle')}
      </p>
      <ChangePasswordForm onDone={noop} />
    </div>
  );
}

function SectionPanel({ section }: { readonly section: Section }): JSX.Element {
  switch (section) {
    case 'profile':
      return <ProfilePanel />;
    case 'therapyTypes':
      return <TherapyTypesPanel />;
    case 'rooms':
      return <RoomsPanel />;
    case 'sessionRules':
      return <SessionRulesPanel />;
    case 'billing':
      return <BillingPanel />;
    case 'notifications':
      return <NotificationsPanel />;
    case 'security':
      return <SecurityPanel />;
  }
}

function SettingsCard({
  card,
  onOpen,
}: {
  readonly card: CardDef;
  readonly onOpen: (card: CardDef) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const open = useCallback(() => {
    onOpen(card);
  }, [onOpen, card]);
  const Icon = card.icon;
  return (
    <button
      type="button"
      onClick={open}
      className="focus-ring flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left hover:border-[var(--accent)] dark:border-slate-700 dark:bg-slate-900"
    >
      <span className="bg-[var(--accent)]/10 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[var(--accent-strong)]">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-slate-800 dark:text-white">
          {t(`settings.cards.${card.key}.title`)}
        </span>
        <span className="block text-sm text-slate-500 dark:text-slate-400">
          {t(`settings.cards.${card.key}.subtitle`)}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" aria-hidden="true" />
    </button>
  );
}

/** Admin → Settings (Figma 6-card grid; each card opens a sub-panel). */
export function AdminSettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section | null>(null);
  const onOpen = useCallback(
    (card: CardDef) => {
      if (card.to !== undefined) {
        void navigate(card.to);
        return;
      }
      setSection(card.key as Section);
    },
    [navigate],
  );
  const back = useCallback(() => {
    setSection(null);
  }, []);

  if (section !== null) {
    return (
      <div className="p-8">
        <button
          type="button"
          onClick={back}
          className="focus-ring mb-4 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {t('settings.title')}
        </button>
        <h1 className="mb-5 text-xl font-bold text-slate-800 dark:text-white">
          {t(`settings.cards.${section}.title`)}
        </h1>
        <SectionPanel section={section} />
      </div>
    );
  }

  return (
    <div className="p-8">
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {CARDS.map((card) => (
          <SettingsCard key={card.key} card={card} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}
